import {Injectable} from '@angular/core';
import {LookupService} from '../../../../common/service/lookup.service';
import {PlanModelService} from '../../../../common/service/plan-model.service';
import {SpatialUtilService} from '../../spatial-util.service';
import {PrintAreaService} from '../../../../common/service/print-area.service';
import {
    calcBearing,
    calculateRotation,
    ddToArcSeconds,
    ddToDms,
    decimalFormattedAngle,
    dmsToDd,
    formattedAngle,
    getAngleBearingAndRotationDetails,
    getBearing,
    getPoint,
    isBearingLarger,
    subtract,
    subtractAngleFrom360,
    subtractBearings,
    toColonDms,
    toDotDms
} from 'src/app/design/services/features/angle/angle-tools';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 *  TODO PAU-727 refactor AngleUtilService. Remove wrapped method from angle-tools
 */
@Injectable({
    providedIn: 'root'
})
export class AngleUtilService {
    title = 'AngleUtilService';
    defaultAttributes: Record<string, any> = {};

    constructor(
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private printAreaService: PrintAreaService,
        private esriModuleService: EsriModuleService
    ) {
        this.setupDefaultAngleAttributes();
    }

    /**
     * @depricated Use angle-tools instead
     */
    ddToDms(dd: number) {
        return ddToDms(dd);
    }

    /**
     * @depricated Use angle-tools instead
     */
    dmsToDd(dms: any) {
        return dmsToDd(dms);
    }

    /**
     * @depricated Use angle-tools instead
     */
    ddToArcSeconds(dd: number) {
        return ddToArcSeconds((dd % 360) * 3600);
    }

    /**
     * @depricated Use angle-tools instead
     */
    dmsToArcSeconds(dms: any) {
        return ddToArcSeconds(dmsToDd(dms));
    }

    /**
     * @depricated Use angle-tools instead
     */
    getBearing(pointBearings: any, indexOfSortedBearing: number) {
        return getBearing(pointBearings, indexOfSortedBearing);
    }

    /**
     * @depricated Use angle-tools instead
     */
    calcBearing(x1: number, y1: number, x2: number, y2: number) {
        return calcBearing(x1, y1, x2, y2);
    }

    /**
     * @depricated Use angle-tools instead
     */
    calcBearingDms(x1: number, y1: number, x2: number, y2: number) {
        return this.ddToDms(this.calcBearing(x1, y1, x2, y2));
    }

    getLineBearing(line: any) {
        console.debug(`${this.title} : getLineBearing`);
        const pointsRelated = this.getPointsRelated([line]);

        return calcBearing(
            getPoint(pointsRelated, line.attributes.frompointid, line.geometry.paths[0][0])[0],
            getPoint(pointsRelated, line.attributes.frompointid, line.geometry.paths[0][0])[1],
            getPoint(pointsRelated, line.attributes.topointid, line.geometry.paths[0][1])[0],
            getPoint(pointsRelated, line.attributes.topointid, line.geometry.paths[0][1])[1]
        );
    }

    /**
     * @depricated Use angle-tools instead
     */
    subtractBearings(bearing: any, bearingToSubtract: any) {
        return subtractBearings(bearing, bearingToSubtract);
    }

    /**
     * @depricated Use angle-tools instead
     */
    subtract(angle: any, angleToSubtract: any) {
        return subtract(angle, angleToSubtract);
    }

    /**
     * @depricated Use angle-tools instead
     */
    subtractAngleFrom360(angle: any) {
        return subtractAngleFrom360(angle);
    }

    /**
     * @depricated Use angle-tools instead
     */
    calculateRotation(angle: any) {
        return calculateRotation(angle);
    }

    /**
     * @depricated Use angle-tools instead
     */
    getPoint(points: any, id: any, data: any) {
        return getPoint(points, id, data);
    }

    /**
     * @depricated Use angle-tools instead
     */
    getAngleBearingAndRotationDetails(point: any, fromLine: any, toLine: any, internal: any, points: any) {
        return getAngleBearingAndRotationDetails(point, fromLine, toLine, internal, points);
    }

    /**
     * @depricated Use angle-tools instead
     */
    toDotDms(angle: any) {
        return toDotDms(angle);
    }

    /**
     * @depricated Use angle-tools instead
     */
    toColonDms(angle: any) {
        return toColonDms(angle);
    }

    formattedAngle(dmsDegrees: any, dmsMinutes: any, dmsSeconds: any) {
        return formattedAngle(dmsDegrees, dmsMinutes, dmsSeconds);
    }

    /**
     * @depricated Use angle-tools instead
     */
    decimalFormattedAngle(dmsDegrees: any, dmsMinutes: any, dmsSeconds: any) {
        return decimalFormattedAngle(dmsDegrees, dmsMinutes, dmsSeconds);
    }

    /**
     * @depricated Use angle-tools instead
     */
    isBearingLarger(bearingToTest: any, bearingToCompare: any) {
        return isBearingLarger(bearingToTest, bearingToCompare);
    }

    getPointsRelated(lines: any[]) {
        const pointsRelated = [];
        const pointsRelatedFoundTwice = [];

        this.spatialUtilService.getPoints().forEach(point => {
            lines.forEach(line => {
                if (line.attributes.frompointid === point.attributes.id || line.attributes.topointid === point.attributes.id) {
                    if (pointsRelated.indexOf(point) !== -1 && pointsRelatedFoundTwice.indexOf(point) === -1) {
                        pointsRelatedFoundTwice.push(point);
                    }

                    pointsRelated.push(point);
                }
            });
        });
        return pointsRelated;
    }

    completeAnglesCreation(angles: any[], points: any[], lines: any[], internalSide: boolean, importFileName: any) {
        console.debug(this.title + ' : completeAnglesCreation');

        const output = [];
        const pointsRelated = this.getPointsRelated(lines);

        angles.forEach(angle => {
            let point = {};
            let fromLine = {};
            let toLine = {};

            points.forEach(pointFeature => {
                if (pointFeature.attributes.id === angle.attributes.pointid) {
                    point = pointFeature;
                }
            });

            lines.forEach(line => {
                if (line.attributes.id === angle.attributes.anglefromlineid) {
                    fromLine = line;
                }

                if (line.attributes.id === angle.attributes.angletolineid) {
                    toLine = line;
                }
            });

            const angleBearingAndRotationDetails = this.getAngleBearingAndRotationDetails(point, fromLine, toLine, internalSide, pointsRelated);

            const createdAngle = this.createAngleObject(point, fromLine, toLine, angleBearingAndRotationDetails.rotation, angle.attributes);

            if (!createdAngle.attributes.internalangle) {
                createdAngle.attributes.internalangle = this.ddToDms(angleBearingAndRotationDetails.bearingBetweenLines);
            } else {
                createdAngle.attributes.internalangle = this.ddToDms(createdAngle.attributes.internalangle);
            }

            const internalAngleDd = this.dmsToDd(createdAngle.attributes.internalangle);
            if (internalAngleDd === 180) {
                createdAngle.attributes.visible = 'NO';
            }

            if (importFileName) {
                createdAngle.attributes.importfilename = importFileName;
            }

            output.push(createdAngle);
        });

        return output;
    }

    createAngleObject(point: {geometry?: any}, fromLine: object, toLine: object, rotation: number, attributes: any) {
        console.debug(this.title + ' : createAngleObject');

        const pointGeometry = new (window as any).esri.geometry.Point(point.geometry.x, point.geometry.y);
        pointGeometry.setSpatialReference(point.geometry.spatialReference);

        const angle = new (window as any).esri.Graphic(pointGeometry);
        angle.geometry = this.getAnglePointOffset(angle, rotation);
        angle.attributes = this.getDefaultAngleAttributes(attributes);
        angle.attributes.guid = this.planModelService.getGuid();
        angle.attributes.x = angle.geometry.x;
        angle.attributes.y = angle.geometry.y;
        angle.attributes.origx = angle.geometry.x;
        angle.attributes.origy = angle.geometry.y;
        angle.attributes.anglerotation = rotation;
        angle.attributes.anglerotationdms = this.ddToDms(rotation);
        angle.attributes.type = this.getAngleType(fromLine, toLine);
        angle.attributes.visible = 'YES';

        Object.entries(angle.attributes).forEach(([property, value]) => {
            if (value && (value as any).id) {
                angle.attributes[property] = (value as any).id;
            }
        });

        return angle;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getAnglePointOffset(pointGraphic: {geometry: any}, offsetBearing: number) {
        console.debug(this.title + ' : getAnglePointOffset');

        const offsetLookup = this.lookupService
            .getLookups('featureOffsetSettings')
            .find(lookup => lookup.validCoordSystemIds.includes(this.planModelService.getCoordinateSystem().id));
        const offsetFactor = this.printAreaService.getReferenceScaleDivisor() * offsetLookup.angleOffset;

        const original = pointGraphic.geometry;
        const offsetX = offsetFactor * Math.sin((offsetBearing * Math.PI) / 180);
        const offsetY = offsetFactor * Math.cos((offsetBearing * Math.PI) / 180);

        return new this.esriModuleService.Point({
            x: original.x + offsetX,
            y: original.y + offsetY,
            spatialReference: original.spatialReference
        });
    }

    getAngleTypeByLines(lineFromFeature: any, lineToFeature: any) {
        console.debug(this.title + ' : getAngleTypeByLines');
        return this.getAngleType(lineFromFeature, lineToFeature);
    }

    getAngleType(fromLine: {attributes?: any}, toLine: {attributes?: any}) {
        console.debug(this.title + ' : getAngleTypeByLines');

        let output = undefined;

        this.lookupService.getLookups('permittedAnglesFromToLineTypes').forEach(rowConfig => {
            const lineTypesFrom = rowConfig.lineTypesFrom.split(',');
            const lineTypesTo = rowConfig.lineTypesTo.split(',');

            if (
                !output &&
                lineTypesFrom.indexOf(String(fromLine.attributes.type)) !== -1 &&
                lineTypesTo.indexOf(String(toLine.attributes.type)) !== -1
            ) {
                output = rowConfig.angleType;
            }
        });

        return output;
    }

    removeNotApplicablePoints(points: any[]) {
        console.debug(this.title + ' : removeNonCadastralPoints');
        const pointTypeLookups = [...this.lookupService.getLookups('pointType'), ...this.lookupService.getLookups('controlPointType')];

        return points.filter(p =>
            pointTypeLookups.find(
                l => (l.angleClass === 'CADASTRAL' || l.angleClass === 'INTEREST') && parseInt(l.id) === parseInt(p.attributes.type)
            )
        );
    }

    removeNonCadastralLines(lines: any[], includeInterest: boolean) {
        console.debug(this.title + ' : removeNonCadastralLines');

        const lineTypeLookups = this.lookupService.getLookups('lineType');

        const output = [];
        lines.forEach(line => {
            const lineTypeLookup = lineTypeLookups.find(l => parseInt(l.id) === parseInt(line.attributes.type));

            if (lineTypeLookup && (lineTypeLookup.angleClass === 'CADASTRAL' || (includeInterest && lineTypeLookup.businessClass === 'Interest'))) {
                let result = undefined;
                this.lookupService.getLookups('permittedAnglesFromToLineTypes').forEach(rowConfig => {
                    const lineTypesFrom = rowConfig.lineTypesFrom.split(',');
                    const lineTypesTo = rowConfig.lineTypesTo.split(',');

                    if (
                        (!result && lineTypesFrom.indexOf(String(line.attributes.type)) !== -1) ||
                        lineTypesTo.indexOf(String(line.attributes.type)) !== -1
                    ) {
                        result = true;
                    }
                });

                if (result || includeInterest !== undefined) {
                    output.push(line);
                }
            }
        });

        return output;
    }

    isInterestLine(line: {attributes: {type: string}}) {
        console.debug(this.title + ' : isInterestLine');
        const lineTypeLookup = this.lookupService
            .getLookups('lineType')
            .find(l => l.businessClass === 'Interest' && parseInt(l.id) === parseInt(line.attributes.type));
        return !!lineTypeLookup;
    }

    getRelatedPoints(lines: any[]) {
        console.debug(`${this.title} : getRelatedPoints`);

        const fromPoints = [];
        const toPoints = [];
        let commonPt = [];

        const graphics = [...this.spatialUtilService.getPointGraphics(), ...this.spatialUtilService.getControlPointGraphics()];

        lines.forEach(l => {
            const fromPoint = graphics.find(g => l.attributes.frompointid === g.attributes.id);
            if (fromPoints.includes(fromPoint)) {
                commonPt.push(fromPoint);
            } else {
                fromPoints.push(fromPoint);
            }

            const toPoint = graphics.find(g => l.attributes.topointid === g.attributes.id);
            if (toPoints.includes(toPoint)) {
                commonPt.push(toPoint);
            } else {
                toPoints.push(toPoint);
            }
        });

        if (commonPt.length === 0) {
            commonPt = fromPoints.filter(value => toPoints.includes(value));
        }

        return commonPt;
    }

    getRelatedLines(points: any[], lines: any[]) {
        console.debug(this.title + ' : getRelatedLines');

        const output = [];

        lines.forEach(line => {
            points.forEach(point => {
                if (line.attributes.frompointid === point.attributes.id || line.attributes.topointid === point.attributes.id) {
                    if (output.indexOf(line) === -1) {
                        output.push(line);
                    }
                }
            });
        });

        return output;
    }

    getDefaultAngleAttributes(attributes: any) {
        console.debug(this.title + ' : getDefaultAngleAttributes');

        const output = attributes ? window.structuredClone(attributes) : {};
        Object.entries(this.defaultAttributes).forEach(([defaultAttributeProp, defaultAttributeValue]) => {
            if (!output || !Object.prototype.hasOwnProperty.call(output, defaultAttributeProp) || output[defaultAttributeProp] === null) {
                output[defaultAttributeProp] = defaultAttributeValue;
            }
        });

        return output;
    }

    setupDefaultAngleAttributes() {
        console.debug(this.title + ' : setupDefaultAngleAttributes');

        this.defaultAttributes = {};

        const featureAttributesLookups = this.lookupService.getLookups('featureAttributes');

        featureAttributesLookups.forEach(attribute => {
            if (attribute.featureType === 'angle' && attribute.featureClassAttribute === true) {
                if (
                    attribute.attributeType.indexOf('RADIO') !== -1 ||
                    attribute.attributeType.indexOf('STRING') !== -1 ||
                    attribute.attributeType === 'BOOLEAN'
                ) {
                    this.defaultAttributes[attribute.prop] = attribute.lookupDefaultId;
                } else if (attribute.attributeType.indexOf('DECIMAL') !== -1) {
                    this.defaultAttributes[attribute.prop] = parseFloat(attribute.lookupDefaultId);
                } else if (attribute.attributeType.indexOf('INTEGER') !== -1) {
                    this.defaultAttributes[attribute.prop] = parseInt(attribute.lookupDefaultId);
                } else {
                    this.defaultAttributes[attribute.prop] = attribute.lookupDefaultId === '' ? null : attribute.lookupDefaultId;
                }
            }
        });

        Object.entries(this.defaultAttributes).forEach(([defaultAttributeProp, defaultAttributeValue]) => {
            if (defaultAttributeValue === undefined || defaultAttributeValue === null || String(defaultAttributeValue) === 'NaN') {
                delete this.defaultAttributes[defaultAttributeProp];
            }
        });
    }
}
